import openedEyeIcon from '@iconify/icons-ant-design/eye-filled';
import closedEyeIcon from '@iconify/icons-ant-design/eye-invisible-outlined';
import infoCircleFilled from '@iconify/icons-ant-design/info-circle-filled';
import { Icon } from '@iconify/react';
import ClearFiltersIcon from '@/../img/brush.svg';
import ExpandAllIcon from '@/../img/triple-chevron-down.svg';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  ButtonGroup,
  CheckPicker,
  DOMHelper,
  Checkbox as RsCheckbox,
  Slider,
  Tooltip,
  Whisper,
} from 'rsuite';
import {
  COMPLAINTS_TYPES,
  PLATFORM_FILTER_OPT_NONE,
  PROPERTY_DATA_FILTER_KEYS,
  PROPERTY_DATA_KV_MAP,
} from '@/constants';
import {
  hoaDropdownOpts,
  licenseTypes,
  rentalTypes,
} from '@/data/data-selectors';
import { SIDEBAR_SECTION_HEADERS } from '@/map/map-constants';
import { hoaFilter } from '@/map/map-selectors';
import {
  getPlatformFriendlyName,
  numberWithCommas,
} from '@/utils/string-utils';
import styled from 'styled-components';
import {
  IconSizer,
  MasterSwitchWrapper,
  Spacer,
  SwitchName,
  SwitchWrapper,
} from './map-sidebar-sections-styles';
import * as sidebar from './map-siderbar-styles';

const InnerWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledInfoIcon = styled(Icon)`
  margin-left: 5px;
  cursor: help;
  color: #37a3bc;
`;

function Checkbox(props) {
  const isChecked = Boolean(props.checked);
  const eyeIcon = isChecked ? openedEyeIcon : closedEyeIcon;

  return (
    <sidebar.ItemWrapper eye={props.eye}>
      <RsCheckbox
        value={props.value}
        checked={isChecked}
        onClick={(evt) => evt.stopPropagation()}
        onChange={(value, checked) =>
          props.onChange(props.groupName, value, checked)
        }
      >
        <InnerWrapper>
          {props.eye && <sidebar.StyledInlineIcon icon={eyeIcon} />}
          {props.label}
          {Boolean(props.tooltip) && (
            <Whisper
              placement="bottomEnd"
              trigger="hover"
              speaker={<Tooltip>{props.tooltip}</Tooltip>}
            >
              <StyledInfoIcon icon={infoCircleFilled} width="16" height="16" />
            </Whisper>
          )}
        </InnerWrapper>
      </RsCheckbox>
    </sidebar.ItemWrapper>
  );
}

export function FilterResultSection(props) {
  return (
    <sidebar.FilterResultWrapper>
      <sidebar.ResultNumber>
        {props.searchResultsCount && numberWithCommas(props.searchResultsCount)}
      </sidebar.ResultNumber>
      <Spacer width={5} />
      <sidebar.TotalNumber>
        of{' '}
        {props.totalResultsCount && numberWithCommas(props.totalResultsCount)}
      </sidebar.TotalNumber>
      <sidebar.ResultText>Results</sidebar.ResultText>
    </sidebar.FilterResultWrapper>
  );
}

function Switch(props) {
  const iconSizerRef = React.useRef(null);
  const handleOnClick = () => {
    props.handleSwitchOnClick();
    if (props.upsideDownOnClick) {
      DOMHelper.toggleClass(iconSizerRef.current, 'upside-down');
      DOMHelper.toggleClass(iconSizerRef.current, 'down-upside');
    }
  };

  return (
    <SwitchWrapper onClick={handleOnClick}>
      <IconSizer ref={iconSizerRef} className="down-upside">
        {props.icon}
      </IconSizer>
      <SwitchName>{props.text}</SwitchName>
    </SwitchWrapper>
  );
}

export function MasterSwitch(props) {
  return (
    <MasterSwitchWrapper>
      <Spacer width={24} />
      <Switch
        upsideDownOnClick
        handleSwitchOnClick={props.handleExpandAllOnClick}
        icon={<ExpandAllIcon />}
        text="Expand all"
      />
      <Spacer width={29} />
      <Switch
        handleSwitchOnClick={props.handleClearFiltersOnClick}
        icon={<ClearFiltersIcon />}
        text="Clear Filters"
      />
    </MasterSwitchWrapper>
  );
}

export function ResidentialTypeSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.RESIDENTIAL_TYPE}
      />
      <Checkbox
        label="Primary"
        value={PROPERTY_DATA_KV_MAP.residentialType.primary}
        onChange={() =>
          handleCheckboxOnChange(
            'residentialType',
            PROPERTY_DATA_KV_MAP.residentialType.primary,
            !mapFilterParams.getIn([
              'residentialType',
              PROPERTY_DATA_KV_MAP.residentialType.primary,
            ])
          )
        }
        groupName="residentialType"
        checked={mapFilterParams.getIn([
          'residentialType',
          PROPERTY_DATA_KV_MAP.residentialType.primary,
        ])}
      />
      <Checkbox
        label="Secondary/Investment"
        value={PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment}
        onChange={() =>
          handleCheckboxOnChange(
            'residentialType',
            PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment,
            !mapFilterParams.getIn([
              'residentialType',
              PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment,
            ])
          )
        }
        groupName="residentialType"
        checked={mapFilterParams.getIn([
          'residentialType',
          PROPERTY_DATA_KV_MAP.residentialType.secondaryInvestment,
        ])}
      />
      <Checkbox
        label="Unknown"
        value={PROPERTY_DATA_KV_MAP.residentialType.unknown}
        onChange={() =>
          handleCheckboxOnChange(
            'residentialType',
            PROPERTY_DATA_KV_MAP.residentialType.unknown,
            !mapFilterParams.getIn([
              'residentialType',
              PROPERTY_DATA_KV_MAP.residentialType.unknown,
            ])
          )
        }
        groupName="residentialType"
        checked={mapFilterParams.getIn([
          'residentialType',
          PROPERTY_DATA_KV_MAP.residentialType.unknown,
        ])}
      />
    </StyledSectionWrapper>
  );
}

const AddressTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  height: 32px;
  margin-top: 14px;
  margin-bottom: 24px;

  button.rs-btn-ghost {
    border-color: #37a3bc !important;
  }

  button:first-child {
    border-bottom-left-radius: 4px !important;
    border-top-left-radius: 4px !important;
  }

  button:last-child {
    border-bottom-right-radius: 4px !important;
    border-top-right-radius: 4px !important;
  }
`;

const StyledButton = styled(Button)`
  z-index: ${(props) => (props.isactive ? 2 : 0)};
  background-color: #fff;
  height: 100%;
  border: solid 1px #d9d9d9;
  line-height: ${(props) => (props.isactive ? 1.3 : 1.2)};

  :hover {
    color: #37a3bc;
  }
`;

const FilterSectionButtonGroup = ({
  activeButton,
  buttonLabels,
  handleOnClick,
}) => {
  const isActive = (button) => button === activeButton;
  const getAppearance = (button) => (button === activeButton ? 'ghost' : null);

  return (
    <StyledButtonGroup>
      {Object.values(buttonLabels).map((status) => (
        <StyledButton
          key={status}
          isactive={+isActive(status)}
          appearance={getAppearance(status)}
          onClick={() => handleOnClick(status)}
        >
          {status}
        </StyledButton>
      ))}
    </StyledButtonGroup>
  );
};

export function AddressIdentificationSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;
  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.ADDRESS_IDENTIFICATION}
      />
      <Checkbox
        label="Identified Properties"
        value={PROPERTY_DATA_KV_MAP.addressIdentification.identifiedListing}
        onChange={handleCheckboxOnChange}
        groupName="addressIdentification"
        checked={mapFilterParams.getIn([
          'addressIdentification',
          PROPERTY_DATA_KV_MAP.addressIdentification.identifiedListing,
        ])}
      />
      <Checkbox
        label="Unidentified Listings"
        value={PROPERTY_DATA_KV_MAP.addressIdentification.unidentifiedListing}
        onChange={handleCheckboxOnChange}
        groupName="addressIdentification"
        checked={mapFilterParams.getIn([
          'addressIdentification',
          PROPERTY_DATA_KV_MAP.addressIdentification.unidentifiedListing,
        ])}
      />
    </StyledSectionWrapper>
  );
}

export function ListingVisibilitySection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;
  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.LISTING_VISIBILITY}
      />
      <Checkbox
        label="Live Now"
        value={PROPERTY_DATA_KV_MAP.visibility.liveNow}
        onChange={handleCheckboxOnChange}
        groupName="visibility"
        checked={mapFilterParams.getIn([
          'visibility',
          PROPERTY_DATA_KV_MAP.visibility.liveNow,
        ])}
      />
      <Checkbox
        label="Live Recently"
        value={PROPERTY_DATA_KV_MAP.visibility.liveRecently}
        onChange={handleCheckboxOnChange}
        groupName="visibility"
        checked={mapFilterParams.getIn([
          'visibility',
          PROPERTY_DATA_KV_MAP.visibility.liveRecently,
        ])}
      />
      <Checkbox
        label="Not Live"
        value={PROPERTY_DATA_KV_MAP.visibility.notLive}
        onChange={handleCheckboxOnChange}
        groupName="visibility"
        checked={mapFilterParams.getIn([
          'visibility',
          PROPERTY_DATA_KV_MAP.visibility.notLive,
        ])}
      />
    </StyledSectionWrapper>
  );
}

export function RentalTypeSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  const rentalTypesMap = useSelector(rentalTypes);

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.RENTAL_TYPE} />

      {Object.entries(rentalTypesMap).map(([filterValue, filterLabel]) => (
        <Checkbox
          key={filterValue}
          label={filterLabel}
          value={filterValue}
          onChange={handleCheckboxOnChange}
          groupName="rentalType"
          checked={mapFilterParams.getIn(['rentalType', filterValue])}
        />
      ))}
    </StyledSectionWrapper>
  );
}

const UnverifiedPropertiesLabel = styled.span`
  white-space: nowrap;
`;

export function VerificationSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.VERIFICATION} />

      <Checkbox
        label="Verified Properties"
        value={PROPERTY_DATA_KV_MAP.verifiedStatus.isVerified}
        onChange={handleCheckboxOnChange}
        groupName="verifiedStatus"
        checked={mapFilterParams.getIn([
          'verifiedStatus',
          PROPERTY_DATA_KV_MAP.verifiedStatus.isVerified,
        ])}
      />

      <Checkbox
        label={
          <UnverifiedPropertiesLabel>
            Unverified Properties
          </UnverifiedPropertiesLabel>
        }
        tooltip="Includes computer-generated addresses and imports from other systems that have not yet been verified by a person."
        value={PROPERTY_DATA_KV_MAP.verifiedStatus.isUnverified}
        onChange={handleCheckboxOnChange}
        groupName="verifiedStatus"
        checked={mapFilterParams.getIn([
          'verifiedStatus',
          PROPERTY_DATA_KV_MAP.verifiedStatus.isUnverified,
        ])}
      />

      <Checkbox
        label="Pending Properties"
        value={PROPERTY_DATA_KV_MAP.verifiedStatus.isPending}
        onChange={handleCheckboxOnChange}
        groupName="verifiedStatus"
        checked={mapFilterParams.getIn([
          'verifiedStatus',
          PROPERTY_DATA_KV_MAP.verifiedStatus.isPending,
        ])}
      />
    </StyledSectionWrapper>
  );
}

export function DiscreteValueFilterSection(props) {
  const {
    mapFilterParams,
    handleCheckboxOnChange,
    sectionTitle,
    groupName,
    valueOptions,
  } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={sectionTitle} />
      {valueOptions.map(({ label, value }) => (
        <Checkbox
          key={value}
          label={label}
          value={value}
          onChange={handleCheckboxOnChange}
          groupName={groupName}
          checked={mapFilterParams.getIn([groupName, value])}
        />
      ))}
    </StyledSectionWrapper>
  );
}

const STR_LICENSE_STATUS = {
  LICENSED: 'Licensed',
  NO_LICENSE: 'No License',
  ALL: 'ALL',
};

const STR_LICENSE_SELECT = {
  [STR_LICENSE_STATUS.LICENSED]: true,
  [STR_LICENSE_STATUS.NO_LICENSE]: false,
  [STR_LICENSE_STATUS.ALL]: 'ALL',
};

export function STRLicenseSection({ handleFilterChange }) {
  const [activeButton, setActiveButton] = useState(STR_LICENSE_STATUS.ALL);

  const handleOnClick = (status) => {
    setActiveButton(status);
    handleFilterChange(
      'strLicense',
      'hasStrLicense',
      STR_LICENSE_SELECT[status]
    );
  };

  return (
    <AddressTitleWrapper>
      <StyledSectionTitle
        marginBottom="0px"
        sectionTitle={SIDEBAR_SECTION_HEADERS.STR_LICENSE}
      />
      <FilterSectionButtonGroup
        buttonLabels={STR_LICENSE_STATUS}
        activeButton={activeButton}
        handleOnClick={handleOnClick}
      />
    </AddressTitleWrapper>
  );
}

export function NewSTRLicenseSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;
  const labels = useSelector(licenseTypes);

  if (labels?.str?.name && labels?.str?.filterOff) {
    return (
      <StyledSectionWrapper>
        <StyledSectionTitle marginBottom="0px" sectionTitle={labels.str.name} />
        <Checkbox
          label={labels.str.filterOn}
          value={PROPERTY_DATA_KV_MAP.residentialType.primary}
          onChange={() =>
            handleCheckboxOnChange(
              'strLicense',
              PROPERTY_DATA_KV_MAP.strLicense.hasStrLicense,
              !mapFilterParams.getIn([
                'strLicense',
                PROPERTY_DATA_KV_MAP.strLicense.hasStrLicense,
              ])
            )
          }
          groupName="strLicense"
          checked={mapFilterParams.getIn([
            'strLicense',
            PROPERTY_DATA_KV_MAP.strLicense.hasStrLicense,
          ])}
        />
        <Checkbox
          label={labels.str?.filterOff}
          value={PROPERTY_DATA_KV_MAP.residentialType.primary}
          onChange={() =>
            handleCheckboxOnChange(
              'strLicense',
              PROPERTY_DATA_KV_MAP.strLicense.hasNoStrLicense,
              !mapFilterParams.getIn([
                'strLicense',
                PROPERTY_DATA_KV_MAP.strLicense.hasNoStrLicense,
              ])
            )
          }
          groupName="strLicense"
          checked={mapFilterParams.getIn([
            'strLicense',
            PROPERTY_DATA_KV_MAP.strLicense.hasNoStrLicense,
          ])}
        />
      </StyledSectionWrapper>
    );
  }
  return null;
}

const StyledSectionWrapper = styled.div`
  margin: 8px;
  width: 250px;
`;

const StyledSectionTitle = styled.div`
  margin-bottom: ${(props) => props.marginBottom || '6px'};
  font-family: Nunito Sans;
  font-size: 14px;
  font-weight: 600;
  color: #101938;
  :before {
    content: '${(props) => props.sectionTitle}';
  }
`;

export function PropertyLocationSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.PROPERTY_LOCATION}
      />
      <Checkbox
        label="Within boundary"
        value={PROPERTY_DATA_KV_MAP.locationType.within}
        onChange={handleCheckboxOnChange}
        groupName="locationType"
        checked={mapFilterParams.getIn([
          'locationType',
          PROPERTY_DATA_KV_MAP.locationType.within,
        ])}
      />
      <Checkbox
        label="Neighboring"
        value={PROPERTY_DATA_KV_MAP.locationType.neighboring}
        onChange={handleCheckboxOnChange}
        groupName="locationType"
        checked={mapFilterParams.getIn([
          'locationType',
          PROPERTY_DATA_KV_MAP.locationType.neighboring,
        ])}
      />
      <Checkbox
        label="Self-reported"
        value={PROPERTY_DATA_KV_MAP.locationType.selfReported}
        onChange={handleCheckboxOnChange}
        groupName="locationType"
        checked={mapFilterParams.getIn([
          'locationType',
          PROPERTY_DATA_KV_MAP.locationType.selfReported,
        ])}
      />
    </StyledSectionWrapper>
  );
}

export function RentalStructureSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.RENTAL_STRUCTURE}
      />
      <sidebar.CheckboxGroup>
        <Checkbox
          label="Main Structure"
          value={PROPERTY_DATA_KV_MAP.rentalStructure.mainStructure}
          onChange={handleCheckboxOnChange}
          groupName="rentalStructure"
          checked={mapFilterParams.getIn([
            'rentalStructure',
            PROPERTY_DATA_KV_MAP.rentalStructure.mainStructure,
          ])}
        />
        <Checkbox
          label="ADU"
          value={PROPERTY_DATA_KV_MAP.rentalStructure.adu}
          onChange={props.handleCheckboxOnChange}
          groupName="rentalStructure"
          checked={props.mapFilterParams.getIn([
            'rentalStructure',
            PROPERTY_DATA_KV_MAP.rentalStructure.adu,
          ])}
        />
        <Checkbox
          label="Multifamily"
          value={PROPERTY_DATA_KV_MAP.rentalStructure.multiFamily}
          onChange={props.handleCheckboxOnChange}
          groupName="rentalStructure"
          checked={props.mapFilterParams.getIn([
            'rentalStructure',
            PROPERTY_DATA_KV_MAP.rentalStructure.multiFamily,
          ])}
        />
        <Checkbox
          label="Unknown"
          value={PROPERTY_DATA_KV_MAP.rentalStructure.unknown}
          onChange={props.handleCheckboxOnChange}
          groupName="rentalStructure"
          checked={props.mapFilterParams.getIn([
            'rentalStructure',
            PROPERTY_DATA_KV_MAP.rentalStructure.unknown,
          ])}
        />
      </sidebar.CheckboxGroup>
    </StyledSectionWrapper>
  );
}

export function UnitTypeSection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.UNIT_TYPE} />
      <sidebar.CheckboxGroup>
        <Checkbox
          label="Full Unit"
          value={PROPERTY_DATA_KV_MAP.unitType.selfContained}
          onChange={handleCheckboxOnChange}
          groupName="unitType"
          checked={mapFilterParams.getIn([
            'unitType',
            PROPERTY_DATA_KV_MAP.unitType.selfContained,
          ])}
        />
        <Checkbox
          label="Partial"
          value={PROPERTY_DATA_KV_MAP.unitType.room}
          onChange={handleCheckboxOnChange}
          groupName="unitType"
          checked={mapFilterParams.getIn([
            'unitType',
            PROPERTY_DATA_KV_MAP.unitType.room,
          ])}
        />
        <Checkbox
          label="Unknown"
          value={PROPERTY_DATA_KV_MAP.unitType.unknown}
          onChange={handleCheckboxOnChange}
          groupName="unitType"
          checked={mapFilterParams.getIn([
            'unitType',
            PROPERTY_DATA_KV_MAP.unitType.unknown,
          ])}
        />
        <Checkbox
          label="Other"
          value={PROPERTY_DATA_KV_MAP.unitType.other}
          onChange={handleCheckboxOnChange}
          groupName="unitType"
          checked={mapFilterParams.getIn([
            'unitType',
            PROPERTY_DATA_KV_MAP.unitType.other,
          ])}
        />
      </sidebar.CheckboxGroup>
    </StyledSectionWrapper>
  );
}

export function OnlinePlatformsSection(props) {
  const {
    mapFilterParams,
    handleMultipleFilterChanges,
    availablePlatforms,
    platformIcons,
  } = props;

  const platformOptions = availablePlatforms.map((platformId) => ({
    value: platformId,
    label: getPlatformFriendlyName(platformId, platformIcons),
  }));

  const presentedPlatformOptions = [
    ...platformOptions,
    { value: PLATFORM_FILTER_OPT_NONE, label: 'None' },
  ];

  const presentedPlatforms = presentedPlatformOptions.map(({ value }) => value);

  const selectedPlatformsMap = mapFilterParams
    .getIn([PROPERTY_DATA_FILTER_KEYS.onlinePlatforms])
    .toJS();

  const selectedPlatforms = Object.keys(selectedPlatformsMap).filter(
    (platformId) => Boolean(selectedPlatformsMap[platformId])
  );

  const onChangeOnlinePlatformsTypes = useCallback(
    (newSelectedPlatforms) => {
      const newSelectedPlatformsSet = new Set(newSelectedPlatforms);

      const enabledOptions = newSelectedPlatforms.map((platformId) => ({
        groupName: PROPERTY_DATA_FILTER_KEYS.onlinePlatforms,
        type: platformId,
        value: true,
      }));

      const disabledOptions = presentedPlatforms
        .filter((p) => !newSelectedPlatformsSet.has(p))
        .map((platformId) => ({
          groupName: PROPERTY_DATA_FILTER_KEYS.onlinePlatforms,
          type: platformId,
          value: false,
        }));

      handleMultipleFilterChanges([...enabledOptions, ...disabledOptions]);
    },
    [presentedPlatforms, handleMultipleFilterChanges]
  );

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle
        sectionTitle={SIDEBAR_SECTION_HEADERS.ONLINE_PLATFORMS}
      />
      <sidebar.CheckboxGroup>
        <FilterCheckPicker
          options={presentedPlatformOptions}
          selectedValues={selectedPlatforms}
          onSelect={onChangeOnlinePlatformsTypes}
        />
      </sidebar.CheckboxGroup>
    </StyledSectionWrapper>
  );
}

function FilterCheckPicker({ options, selectedValues, onSelect }) {
  const checkPickerCointainerRef = useRef();
  return (
    <>
      <CheckPicker
        sticky
        data={options || []}
        value={selectedValues}
        css="width: 100%;"
        block
        preventOverflow
        // check global styles for menu styling
        menuClassName="filter-section-check-picker"
        // prevents picker portaling out of popover
        container={() => checkPickerCointainerRef.current}
        onSelect={onSelect}
        onClean={() => onSelect([])}
      />
      <div css="position: relative;" ref={checkPickerCointainerRef} />
    </>
  );
}

export function ViolationsSection(props) {
  const { mapFilterParams, handleSingleFilterChange, availableViolations } =
    props;

  const violationTypeOpts = useMemo(
    () => availableViolations.map((value) => ({ label: value, value })),
    [availableViolations]
  );

  const selectedViolationTypeValues =
    mapFilterParams.getIn([
      PROPERTY_DATA_FILTER_KEYS.violations,
      PROPERTY_DATA_KV_MAP.violations.violationTypes,
    ]) || [];

  const onChangeViolationTypes = useCallback(
    (selectedTypes) => {
      handleSingleFilterChange(
        PROPERTY_DATA_FILTER_KEYS.violations,
        PROPERTY_DATA_KV_MAP.violations.violationTypes,
        selectedTypes
      );
    },
    [handleSingleFilterChange]
  );

  const isHasViolationsChecked = mapFilterParams.getIn([
    PROPERTY_DATA_FILTER_KEYS.violations,
    PROPERTY_DATA_KV_MAP.violations.hasViolations,
  ]);

  const isHasNoViolationsChecked = mapFilterParams.getIn([
    PROPERTY_DATA_FILTER_KEYS.violations,
    PROPERTY_DATA_KV_MAP.violations.hasNoViolations,
  ]);

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.VIOLATIONS} />
      <sidebar.CheckboxGroup>
        <Checkbox
          groupName="violations"
          label="Has Violations"
          value={PROPERTY_DATA_KV_MAP.violations.hasViolations}
          onChange={handleSingleFilterChange}
          checked={isHasViolationsChecked}
        />
        <Checkbox
          groupName="violations"
          label="No Violations"
          value={PROPERTY_DATA_KV_MAP.violations.hasNoViolations}
          onChange={handleSingleFilterChange}
          checked={isHasNoViolationsChecked}
        />

        <FilterCheckPicker
          options={violationTypeOpts}
          selectedValues={selectedViolationTypeValues}
          onSelect={onChangeViolationTypes}
        />
      </sidebar.CheckboxGroup>
    </StyledSectionWrapper>
  );
}

const complaintTypeOpts = Object.keys(COMPLAINTS_TYPES).map(
  (complaintType) => ({
    value: complaintType,
    label: COMPLAINTS_TYPES[complaintType],
  })
);

export function ComplaintsSection(props) {
  const { mapFilterParams, handleSingleFilterChange } = props;

  const selectedComplaintTypeValues =
    mapFilterParams.getIn([
      PROPERTY_DATA_FILTER_KEYS.complaints,
      PROPERTY_DATA_KV_MAP.complaints.complaintTypes,
    ]) || [];

  const onChangeComplaintTypes = useCallback(
    (selectedTypes) => {
      handleSingleFilterChange(
        PROPERTY_DATA_FILTER_KEYS.complaints,
        PROPERTY_DATA_KV_MAP.complaints.complaintTypes,
        selectedTypes
      );
    },
    [handleSingleFilterChange]
  );

  const isHasComplaintsChecked = mapFilterParams.getIn([
    PROPERTY_DATA_FILTER_KEYS.complaints,
    PROPERTY_DATA_KV_MAP.complaints.hasComplaints,
  ]);

  const isHasNoComplaintsChecked = mapFilterParams.getIn([
    PROPERTY_DATA_FILTER_KEYS.complaints,
    PROPERTY_DATA_KV_MAP.complaints.hasNoComplaints,
  ]);

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.COMPLAINTS} />
      <sidebar.CheckboxGroup>
        <Checkbox
          groupName="complaints"
          label="Has Complaints"
          value={PROPERTY_DATA_KV_MAP.complaints.hasComplaints}
          onChange={handleSingleFilterChange}
          checked={isHasComplaintsChecked}
        />
        <Checkbox
          groupName="complaints"
          label="No Complaints"
          value={PROPERTY_DATA_KV_MAP.complaints.hasNoComplaints}
          onChange={handleSingleFilterChange}
          checked={isHasNoComplaintsChecked}
        />

        <FilterCheckPicker
          options={complaintTypeOpts}
          selectedValues={selectedComplaintTypeValues}
          onSelect={onChangeComplaintTypes}
        />
      </sidebar.CheckboxGroup>
    </StyledSectionWrapper>
  );
}

export function OccupancySection(props) {
  const { mapFilterParams, handleSliderChange } = props;

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.OCCUPANCY} />
      <sidebar.OccupancyCheckboxWrapper>
        <RsCheckbox
          onClick={(evt) => evt.stopPropagation()}
          checked={mapFilterParams.getIn(['occupancy', 'withUnknown'])}
          onChange={() =>
            handleSliderChange(
              'occupancy',
              'withUnknown',
              !mapFilterParams.getIn(['occupancy', 'withUnknown'])
            )
          }
        >
          Exclude Unknown
        </RsCheckbox>
      </sidebar.OccupancyCheckboxWrapper>

      <sidebar.OccupancySliderWrapper>
        <sidebar.OccupancySliderBar>
          <Slider
            progress
            value={mapFilterParams.getIn(['occupancy', 'numberValue'])}
            min={0}
            max={100}
            onClick={(evt) => evt.stopPropagation()}
            onChange={(value) =>
              handleSliderChange('occupancy', 'numberValue', value)
            }
          />
        </sidebar.OccupancySliderBar>
        <sidebar.OccupancySliderValue
          value={mapFilterParams.getIn(['occupancy', 'numberValue'])}
        >
          ≥&nbsp;{mapFilterParams.getIn(['occupancy', 'numberValue'])}%
        </sidebar.OccupancySliderValue>
      </sidebar.OccupancySliderWrapper>
    </StyledSectionWrapper>
  );
}

export function HoaSection({ onChange, onClean }) {
  const checkPickerContainerRef = useRef();
  const hoaOpts = useSelector(hoaDropdownOpts);

  /**
   * The return value is an Immutable List.
   * Refer to "initFilterParams.hoa" and the definition of the "hoaFilter" selector.
   */
  const selectedHOAs = useSelector(hoaFilter);
  const selectedHOAsJSON = selectedHOAs.toJSON();

  if (!hoaOpts?.length) {
    return null;
  }

  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.ZONE} />
      <CheckPicker
        className="hoa-filter-section-checkpicker"
        sticky
        data={hoaOpts || []}
        value={selectedHOAsJSON}
        css="width: 100%;"
        preventOverflow
        // check global styles for menu styling
        menuClassName="filter-section-check-picker"
        // prevents picker portaling out of popover
        container={() => checkPickerContainerRef.current}
        onSelect={onChange}
        onClean={onClean}
      />
      <div css="position: relative;" ref={checkPickerContainerRef} />
    </StyledSectionWrapper>
  );
}

export function ActivitySection(props) {
  const { mapFilterParams, handleCheckboxOnChange } = props;
  return (
    <StyledSectionWrapper>
      <StyledSectionTitle sectionTitle={SIDEBAR_SECTION_HEADERS.ACTIVITY} />
      <Checkbox
        label="Has Bookings"
        tooltip="Listing calendar(s) indicate bookings in the future"
        value={PROPERTY_DATA_KV_MAP.activity.hasBookings}
        onChange={handleCheckboxOnChange}
        groupName="activity"
        checked={mapFilterParams.getIn([
          'activity',
          PROPERTY_DATA_KV_MAP.activity.hasBookings,
        ])}
      />
      <Checkbox
        label="Has Availability"
        tooltip="Listing calendar(s) don't contain future bookings, but do have future availability"
        value={PROPERTY_DATA_KV_MAP.activity.hasAvailability}
        onChange={handleCheckboxOnChange}
        groupName="activity"
        checked={mapFilterParams.getIn([
          'activity',
          PROPERTY_DATA_KV_MAP.activity.hasAvailability,
        ])}
      />
      <Checkbox
        label="Is Blocked"
        tooltip="Listing calendar(s) currently contain no future bookings or availability"
        value={PROPERTY_DATA_KV_MAP.activity.isBlocked}
        onChange={handleCheckboxOnChange}
        groupName="activity"
        checked={mapFilterParams.getIn([
          'activity',
          PROPERTY_DATA_KV_MAP.activity.isBlocked,
        ])}
      />
    </StyledSectionWrapper>
  );
}
